import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Editor } from '@tiptap/react';
import { DOMParser as PmDOMParser } from '@tiptap/pm/model';
import {
  Sparkles, ChevronDown, ChevronRight, Loader,
  Maximize2, Minimize2, ArrowLeft, RefreshCw,
  LockKeyhole, PenLine,
} from 'lucide-react';
import { exportToWord, exportToPdf } from '../../utils/exportReport';
import { type Report, reportApi, agentApi } from '../../services/reportApi';
import { ReportEditor } from '../ReportEditor';
import { SaveStatusIndicator } from '../SaveStatusIndicator';
import { useAutosave } from '../../hooks/useAutosave';
import { useAskSidebar } from '../../contexts/AskSidebarContext';
import { markdownToHtml } from '../../utils/markdownToProseMirror';
import { SECTION_STYLE_GUIDANCE } from '../../utils/sectionGuidance';
import type { ReportPreferences } from './ConversationalSetupView';

/** Strip PII marker tags from generated text, keeping only the inner value. */
function stripPiiMarkers(text: string): string {
  return text.replace(/\{\{PII:[A-Z_]+\}\}/g, '').replace(/\{\{\/P(?:II|PI)(?::[A-Z_]+)?\}\}/g, '');
}

/**
 * Strip markdown headings (## ...) from generated text so the agent's output
 * doesn't inject H2 tags that corrupt the document's heading structure.
 */
function stripMarkdownHeadings(text: string): string {
  return text.replace(/^#{1,6}\s+.*$/gm, (match) => {
    // Keep the text content, just remove the heading markers
    return match.replace(/^#{1,6}\s+/, '**') + '**';
  });
}

/**
 * Strip agent preamble/postamble — lines like "I now have comprehensive
 * information..." or "Here it is:" that the LLM emits before the actual content.
 */
function stripAgentPreamble(text: string): string {
  // Remove common preamble patterns at the start
  let cleaned = text.replace(
    /^(?:(?:I (?:now |have |will |can ).*?(?:section|report|information).*?[.:])\s*(?:Here (?:it is|is the)[.:])?\s*\n*)+/i,
    '',
  );
  // Also remove standalone "Here it is:" / "Here is the ... section:" lines
  cleaned = cleaned.replace(/^Here (?:it is|is the [^:]+)[.:]\s*\n*/im, '');
  return cleaned.trim();
}


// ─── Section state ──────────────────────────────────────────────────────────
export interface SectionState {
  id: string;
  title: string;
  placeholder?: string;
  sectionType?: 'static' | 'variable' | 'placeholder';
  status: 'empty' | 'generating' | 'draft' | 'edited';
}

interface SectionManagerViewProps {
  report: Report;
  sections: SectionState[];
  caseId: string;
  onBack: () => void;
  preferences?: ReportPreferences;
}

// ─── Status badge ───────────────────────────────────────────────────────────
const STATUS_STYLES: Record<SectionState['status'], { bg: string; text: string; label: string }> = {
  empty:      { bg: 'bg-medical-100 dark:bg-medical-700', text: 'text-medical-500 dark:text-medical-400', label: 'Empty' },
  generating: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', label: 'Generating' },
  draft:      { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', label: 'Draft' },
  edited:     { bg: 'bg-primary-100 dark:bg-primary-900/30', text: 'text-primary-700 dark:text-primary-300', label: 'Edited' },
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Find a section's H2 heading by title in the top-level doc nodes.
 * Returns the node index in docJson.content, or -1 if not found.
 */
function findSectionIndex(docJson: any, sectionTitle: string): number {
  const nodes = docJson.content ?? [];
  return nodes.findIndex(
    (n: any) => n.type === 'heading' && n.attrs?.level === 2
      && n.content?.[0]?.text?.trim() === sectionTitle,
  );
}

/**
 * Find the index of the next H2 heading after `afterIdx`, or the array length
 * if there is no subsequent H2.
 */
function findNextH2Index(docJson: any, afterIdx: number): number {
  const nodes = docJson.content ?? [];
  for (let i = afterIdx + 1; i < nodes.length; i++) {
    if (nodes[i].type === 'heading' && nodes[i].attrs?.level === 2) {
      return i;
    }
  }
  return nodes.length;
}

/**
 * Extract sub-heading titles from a section's body.
 * Looks for H3 headings and bold-only paragraphs (used as sub-topics).
 */
function extractSubHeadings(docJson: any, sectionTitle: string): string[] {
  const idx = findSectionIndex(docJson, sectionTitle);
  if (idx === -1) return [];
  const nextIdx = findNextH2Index(docJson, idx);
  const subs: string[] = [];
  for (let i = idx + 1; i < nextIdx; i++) {
    const node = docJson.content[i];
    // H3 headings
    if (node.type === 'heading' && node.attrs?.level === 3) {
      const text = (node.content ?? []).map((c: any) => c.text ?? '').join('').trim();
      if (text) subs.push(text);
      continue;
    }
    // Bold-only paragraphs (entire paragraph is a single bold run — used as sub-topic headers)
    if (node.type === 'paragraph' && node.content?.length >= 1) {
      const allBold = node.content.every(
        (c: any) => c.type === 'text' && c.marks?.some((m: any) => m.type === 'bold'),
      );
      if (allBold) {
        const text = node.content.map((c: any) => c.text ?? '').join('').trim();
        // Only treat short paragraphs as sub-headings (not entire bold paragraphs of content)
        if (text && text.length <= 120) subs.push(text);
      }
    }
  }
  return subs;
}

/**
 * Check whether a section has any non-empty body content (between its H2 and the next H2).
 */
function sectionHasContent(docJson: any, sectionTitle: string): boolean {
  const idx = findSectionIndex(docJson, sectionTitle);
  if (idx === -1) return false;
  const nextIdx = findNextH2Index(docJson, idx);
  // Body nodes are between idx+1 and nextIdx (exclusive)
  for (let i = idx + 1; i < nextIdx; i++) {
    const node = docJson.content[i];
    // An empty paragraph has no content array or only whitespace
    const text = (node.content ?? []).map((c: any) => c.text ?? '').join('').trim();
    if (text.length > 0) return true;
  }
  return false;
}

/**
 * Replace the body of a section entirely at the JSON level.
 *
 * 1. Export the doc as JSON
 * 2. Find the target H2 by title
 * 3. Parse new HTML → ProseMirror nodes via DOMParser
 * 4. Filter out any H2 nodes from parsed content (safety net)
 * 5. Splice new nodes between the H2 heading and the next H2
 * 6. `setContent` the rebuilt doc
 *
 * This avoids all ProseMirror position arithmetic and insertContent edge cases.
 */
function replaceSectionBody(editor: Editor, sectionTitle: string, html: string): boolean {
  const docJson = editor.getJSON();
  const sectionIdx = findSectionIndex(docJson, sectionTitle);
  if (sectionIdx === -1) return false;

  const nextH2Idx = findNextH2Index(docJson, sectionIdx);

  // Parse HTML → ProseMirror nodes using the editor's schema
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;
  const parsed = PmDOMParser.fromSchema(editor.schema).parse(wrapper);
  const newNodes: any[] = [];
  parsed.content.forEach((node: any) => {
    const json = node.toJSON();
    // Safety net: never inject H2 headings into a section body
    if (json.type === 'heading' && json.attrs?.level === 2) return;
    newNodes.push(json);
  });

  // If parsing produced nothing, insert an empty paragraph
  if (newNodes.length === 0) {
    newNodes.push({ type: 'paragraph' });
  }

  const topNodes = docJson.content ?? [];

  // Rebuild: [before + heading] + [new content] + [next H2 onwards]
  const rebuilt = [
    ...topNodes.slice(0, sectionIdx + 1),
    ...newNodes,
    ...topNodes.slice(nextH2Idx),
  ];

  editor.commands.setContent({ type: 'doc', content: rebuilt });
  return true;
}

/**
 * Find the ProseMirror character position just after a section's H2 heading.
 * Used for scroll-to-section (read-only, no content mutation).
 */
function findSectionInsertPos(editor: Editor, sectionTitle: string): number | null {
  let result: number | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (result !== null) return false;
    if (node.type.name === 'heading' && node.attrs.level === 2) {
      const text = node.textContent.trim();
      if (text === sectionTitle) {
        result = pos + node.nodeSize;
        return false;
      }
    }
  });
  return result;
}

// ─── Component ──────────────────────────────────────────────────────────────

export const SectionManagerView: React.FC<SectionManagerViewProps> = ({
  report,
  sections: initialSections,
  caseId,
  onBack,
  preferences,
}) => {
  const [sections, setSections] = useState<SectionState[]>(initialSections);
  const [sectionsCollapsed, setSectionsCollapsed] = useState(false);
  const [generatingSectionId, setGeneratingSectionId] = useState<string | null>(null);
  const [streamedText, setStreamedText] = useState('');
  const editorRef = useRef<Editor | null>(null);
  const isGeneratingRef = useRef(false);
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  const { isMaximized, toggleMaximize, setPageContext } = useAskSidebar();

  // Autosave
  const { saveStatus, onContentChange, setInitialContent, createSnapshot } = useAutosave({
    reportId: report.id,
  });

  // Set initial content for autosave diffing
  useEffect(() => {
    setInitialContent(report.content);
  }, [report.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Register editor with sidebar context so Assistant tab works
  const handleEditorReady = useCallback((editor: Editor) => {
    editorRef.current = editor;
    setPageContext({
      pageType: 'report-editor',
      caseId,
      reportId: report.id,
      editor,
      editorSelectedText: null,
    });
  }, [caseId, report.id, setPageContext]);

  // Cleanup context on unmount
  useEffect(() => {
    return () => {
      setPageContext({
        pageType: 'case-detail',
        caseId,
        reportId: null,
        editor: null,
        editorSelectedText: null,
      });
    };
  }, [caseId, setPageContext]);

  // Detect manual edits — mark sections as 'edited' when content changes under their heading
  const handleContentUpdate = useCallback((content: string) => {
    onContentChange(content);

    // If not mid-generation, check if any 'empty' section now has content
    if (!generatingSectionId && editorRef.current) {
      const docJson = editorRef.current.getJSON();
      setSections(prev => prev.map(s => {
        if (s.status === 'empty' && sectionHasContent(docJson, s.title)) {
          return { ...s, status: 'edited' };
        }
        return s;
      }));
    }
  }, [onContentChange, generatingSectionId]);

  // Scroll editor to a section heading
  const scrollToSection = useCallback((sectionTitle: string) => {
    if (!editorRef.current) return;
    const pos = findSectionInsertPos(editorRef.current, sectionTitle);
    if (pos !== null) {
      editorRef.current.chain().focus().setTextSelection(pos).run();
      // Scroll the heading into view
      const domPos = editorRef.current.view.domAtPos(pos - 1);
      if (domPos.node instanceof HTMLElement) {
        domPos.node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (domPos.node.parentElement) {
        domPos.node.parentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, []);

  // Scroll to a sub-heading (H3 or bold paragraph) by its text content
  const scrollToSubHeading = useCallback((text: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    const docJson = editor.getJSON();
    const nodes = docJson.content ?? [];
    // Walk the doc to find the node position
    let pos = 0;
    for (const node of nodes) {
      // +1 for the node's opening token
      const nodeText = (node.content ?? []).map((c: any) => c.text ?? '').join('').trim();
      if (nodeText === text) {
        // Found it — set selection and scroll
        editor.chain().focus().setTextSelection(pos + 1).run();
        try {
          const domPos = editor.view.domAtPos(pos + 1);
          const el = domPos.node instanceof HTMLElement ? domPos.node : domPos.node.parentElement;
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch { /* ignore DOM lookup failures */ }
        return;
      }
      pos += editor.view.state.doc.content.child(nodes.indexOf(node)).nodeSize;
    }
  }, []);

  // Extract sub-headings from the current doc (re-derived when sections change)
  const subHeadingsMap = useMemo(() => {
    const editor = editorRef.current;
    if (!editor) return new Map<string, string[]>();
    const docJson = editor.getJSON();
    const map = new Map<string, string[]>();
    for (const section of sections) {
      map.set(section.id, extractSubHeadings(docJson, section.title));
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, report.content]);

  // Export handler — uses server-side export to preserve template formatting
  const handleExport = useCallback(async (format: 'word' | 'pdf') => {
    // Save the latest editor content before exporting
    const editor = editorRef.current;
    if (editor) {
      const docJson = editor.getJSON();
      try {
        await reportApi.updateReport(report.id, { content: JSON.stringify(docJson) });
      } catch (err) {
        console.error('Failed to save before export', err);
      }
    }

    try {
      await reportApi.exportReport(report.id, format === 'word' ? 'docx' : 'pdf');
    } catch (err: any) {
      console.error('Server-side export failed, falling back to client-side', err);
      // Fallback to client-side export if server-side fails
      if (editor) {
        const docJson = editor.getJSON();
        if (format === 'word') {
          exportToWord(docJson, report.title);
        } else {
          exportToPdf(docJson, report.title);
        }
      }
    }
  }, [report.id, report.title]);

  // Track which sections are expanded in the index
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const toggleSectionExpanded = useCallback((sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  // ─── Section generation helpers ────────────────────────────────────────

  /** Build section guide and other-section titles from current state. */
  const buildSectionContext = useCallback((section: SectionState) => {
    const parts: string[] = [];
    if (section.placeholder) parts.push(`Section purpose: ${section.placeholder}.`);
    const styleGuide = SECTION_STYLE_GUIDANCE[section.title];
    if (styleGuide) parts.push(styleGuide);

    // Inject preferences if available
    if (preferences) {
      const prefParts: string[] = [];
      if (preferences.audience) prefParts.push(`Audience: ${preferences.audience}`);
      if (preferences.focusAreas?.length) prefParts.push(`Focus areas: ${preferences.focusAreas.join(', ')}`);
      if (preferences.keyEvents?.length) prefParts.push(`Key events: ${preferences.keyEvents.join(', ')}`);
      if (preferences.scope) prefParts.push(`Scope: ${preferences.scope}`);
      if (preferences.citationStyle) prefParts.push(`Citation style: ${preferences.citationStyle}`);
      if (preferences.additionalGuidance) prefParts.push(preferences.additionalGuidance);
      if (prefParts.length > 0) parts.push(`User preferences: ${prefParts.join('. ')}.`);
    }

    const sectionGuide = parts.filter(Boolean).join('\n');

    const otherSections = sectionsRef.current
      .filter(s => s.id !== section.id)
      .map(s => `${s.title}${s.status === 'draft' || s.status === 'edited' ? ' (already written)' : ''}`);

    return { sectionGuide, otherSections };
  }, [preferences]);

  /** Common pre-flight checks and state setup for generation. Returns the editor or null. */
  const startGeneration = useCallback((section: SectionState): Editor | null => {
    const editor = editorRef.current;
    if (!editor) {
      console.warn(`[generateSection] No editor ref for "${section.title}"`);
      return null;
    }
    if (isGeneratingRef.current) {
      console.warn(`[generateSection] Already generating, skipping "${section.title}"`);
      return null;
    }
    const docJson = editor.getJSON();
    if (findSectionIndex(docJson, section.title) === -1) {
      console.warn(`[generateSection] Could not find heading for "${section.title}" in doc`);
      return null;
    }

    isGeneratingRef.current = true;
    setGeneratingSectionId(section.id);
    setSections(prev => prev.map(s => s.id === section.id ? { ...s, status: 'generating' } : s));
    setStreamedText('');
    return editor;
  }, []);

  /** Common cleanup after generation completes (success or error). */
  const finishGeneration = useCallback((section: SectionState, success: boolean, editor: Editor, accumulated: string) => {
    if (success && accumulated.trim()) {
      const cleaned = stripMarkdownHeadings(stripAgentPreamble(stripPiiMarkers(accumulated)));
      const html = markdownToHtml(cleaned);
      const ok = replaceSectionBody(editor, section.title, html);
      if (!ok) console.warn(`[generateSection] replaceSectionBody failed for "${section.title}"`);
    }

    setSections(prev => prev.map(s =>
      s.id === section.id ? { ...s, status: success ? 'draft' : 'empty' } : s,
    ));
    isGeneratingRef.current = false;
    setGeneratingSectionId(null);
    setStreamedText('');
    if (success) createSnapshot('ai_generation', `Generated "${section.title}" section`);
  }, [createSnapshot]);

  // ─── Primary: context-first generation (single LLM call) ─────────────

  /** Generate a section using pre-gathered context — fast path (~5-10s). */
  const generateSection = useCallback((section: SectionState): Promise<void> => {
    const editor = startGeneration(section);
    if (!editor) return Promise.resolve();

    let accumulated = '';
    const { sectionGuide, otherSections } = buildSectionContext(section);

    return new Promise<void>((resolve) => {
      reportApi.generateSectionStream(
        report.id,
        {
          caseId,
          sectionTitle: section.title,
          sectionGuide: sectionGuide || undefined,
          otherSections,
        },
        (token: string) => {
          accumulated += token;
          setStreamedText(accumulated);
        },
        () => {
          finishGeneration(section, true, editor, accumulated);
          resolve();
        },
        (err: Error) => {
          console.error('Context-first section generation failed:', err);
          finishGeneration(section, false, editor, accumulated);
          resolve();
        },
      );
    });
  }, [caseId, report.id, startGeneration, buildSectionContext, finishGeneration]);

  // ─── Fallback: agent-based generation (multi-turn tool loop) ──────────
  // Shared conversation ID so the agent retains context across sections
  const conversationIdRef = useRef<string | null>(null);

  /** Regenerate a section using the agentic assistant — slower but can discover additional context. */
  const generateSectionViaAgent = useCallback((section: SectionState): Promise<void> => {
    const editor = startGeneration(section);
    if (!editor) return Promise.resolve();

    let accumulated = '';
    const { sectionGuide, otherSections } = buildSectionContext(section);

    const instruction = [
      `Write the "${section.title}" section of this medical-legal expert report.`,
      sectionGuide,
      '',
      `This report has the following other sections — do NOT duplicate their content:\n${otherSections.map(s => `- ${s}`).join('\n')}`,
      '',
      'Use the case documents, chronology, and key findings as evidence. Cite document names in square brackets when referencing evidence (e.g. [original_GP_records.pdf]).',
      '',
      'CRITICAL RULES:',
      '- Return ONLY the section prose. No heading, no preamble like "Here is the section", no explanation.',
      '- Do NOT include any markdown headings (## or ###) — use **bold text** for sub-topics instead.',
      '- Do NOT repeat factual content that belongs in other sections. Each section must serve a distinct purpose.',
      '- Be precise and evidence-based. Every factual claim must cite a source document.',
    ].filter(Boolean).join('\n');

    return new Promise<void>((resolve) => {
      agentApi.chat(
        {
          message: instruction,
          caseId,
          conversationId: conversationIdRef.current,
        },
        {
          onThinking: () => {},
          onToolCall: () => {},
          onToolResult: () => {},
          onToken: (token: string) => {
            accumulated += token;
            setStreamedText(accumulated);
          },
          onDone: (event) => {
            if (event.conversationId) conversationIdRef.current = event.conversationId;
            finishGeneration(section, true, editor, accumulated);
            resolve();
          },
          onError: (err: Error) => {
            console.error('Agent section generation failed:', err);
            finishGeneration(section, false, editor, accumulated);
            resolve();
          },
        },
      );
    });
  }, [caseId, startGeneration, buildSectionContext, finishGeneration]);


  return (
    <div className={`flex flex-col overflow-hidden ${isMaximized ? 'fixed inset-0 z-50 bg-white dark:bg-medical-900' : 'flex-1 min-h-0'}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-medical-100 dark:border-medical-700 bg-white dark:bg-medical-800 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={onBack}
            className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1 flex-shrink-0"
          >
            <ArrowLeft className="h-3 w-3" />
            Back
          </button>
          <span className="text-medical-300 dark:text-medical-600">|</span>
          <span className="text-xs text-medical-600 dark:text-medical-400 truncate">{report.title}</span>
          <SaveStatusIndicator status={saveStatus} />
        </div>
        <button
          onClick={toggleMaximize}
          className="p-1 rounded-md text-medical-400 hover:text-medical-600 dark:hover:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 transition-colors flex-shrink-0"
          title={isMaximized ? 'Minimise' : 'Maximise'}
        >
          {isMaximized ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* Section index */}
      <div className={`border-b border-medical-100 dark:border-medical-700 flex-shrink-0 ${sectionsCollapsed ? '' : 'max-h-64 overflow-y-auto'}`}>
        <button
          onClick={() => setSectionsCollapsed(!sectionsCollapsed)}
          className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-medical-500 dark:text-medical-400 hover:bg-medical-50 dark:hover:bg-medical-700/30"
        >
          <span>Contents</span>
          {sectionsCollapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronDown className={`h-3 w-3 transition-transform rotate-180`} />}
        </button>

        {!sectionsCollapsed && (
          <nav className="pb-1.5">
            {sections.map((section) => {
              const isGenerating = section.status === 'generating';
              const isStatic = section.sectionType === 'static';
              const isPlaceholder = section.sectionType === 'placeholder';
              const isGeneratable = !isStatic && !isPlaceholder;
              const subs = subHeadingsMap.get(section.id) ?? [];
              const hasSubs = subs.length > 0;
              const isExpanded = expandedSections.has(section.id);
              return (
                <div key={section.id}>
                  <div className={`flex items-center gap-1 px-2 py-0.5 group ${isStatic ? 'opacity-60' : ''}`}>
                    {/* Expand toggle */}
                    <button
                      onClick={() => hasSubs && toggleSectionExpanded(section.id)}
                      className={`p-0.5 rounded flex-shrink-0 ${hasSubs ? 'text-medical-400 dark:text-medical-500 hover:text-medical-600 dark:hover:text-medical-400' : 'text-transparent'}`}
                      disabled={!hasSubs}
                      tabIndex={hasSubs ? 0 : -1}
                    >
                      <ChevronRight className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>

                    {/* Section title — clickable link */}
                    <button
                      onClick={() => scrollToSection(section.title)}
                      className={`flex-1 min-w-0 text-left text-xs transition-colors truncate ${
                        isStatic
                          ? 'text-medical-400 dark:text-medical-500'
                          : 'text-medical-700 dark:text-medical-300 hover:text-primary-600 dark:hover:text-primary-400'
                      }`}
                    >
                      {section.title}
                    </button>

                    {/* Section type indicator */}
                    {isStatic && (
                      <span title="Static — kept from original document"><LockKeyhole className="h-3 w-3 text-medical-400 dark:text-medical-500 flex-shrink-0" /></span>
                    )}
                    {isPlaceholder && (
                      <span title="Manual — to be completed by the author"><PenLine className="h-3 w-3 text-cyan-400 dark:text-cyan-500 flex-shrink-0" /></span>
                    )}

                    {/* Generation indicator */}
                    {isGenerating && (
                      <Loader className="h-3 w-3 animate-spin text-amber-500 flex-shrink-0" />
                    )}

                    {/* Hover actions — only for generatable (variable) sections */}
                    {isGeneratable && !generatingSectionId && (section.status === 'empty' || section.status === 'edited') && (
                      <button
                        onClick={() => generateSection(section)}
                        className="p-0.5 rounded text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        title={`Generate "${section.title}"`}
                      >
                        <Sparkles className="h-3 w-3" />
                      </button>
                    )}
                    {isGeneratable && !generatingSectionId && section.status === 'draft' && (
                      <button
                        onClick={() => generateSectionViaAgent(section)}
                        className="p-0.5 rounded text-amber-500 hover:text-amber-700 dark:hover:text-amber-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        title={`Regenerate "${section.title}"`}
                      >
                        <RefreshCw className="h-3 w-3" />
                      </button>
                    )}
                  </div>

                  {/* Sub-headings (expanded) */}
                  {isExpanded && subs.map((sub, i) => (
                    <button
                      key={`${section.id}-sub-${i}`}
                      onClick={() => scrollToSubHeading(sub)}
                      className="w-full text-left pl-9 pr-3 py-0.5 text-[11px] text-medical-500 dark:text-medical-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors truncate"
                    >
                      {sub}
                    </button>
                  ))}
                </div>
              );
            })}
          </nav>
        )}
      </div>

      {/* Streaming preview (shown during generation) */}
      {generatingSectionId && (
        <div className="px-3 py-1.5 bg-amber-50 dark:bg-amber-900/10 border-b border-amber-200 dark:border-amber-800/30 flex-shrink-0">
          <p className="text-[10px] text-amber-600 dark:text-amber-400 truncate">
            {streamedText.length > 0
              ? `Writing… ${streamedText.length} chars`
              : 'Analysing case records…'}
          </p>
        </div>
      )}

      {/* Editor */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        <ReportEditor
          content={report.content}
          reportId={report.id}
          caseId={caseId}
          onUpdate={handleContentUpdate}
          onEditorReady={handleEditorReady}
          onExport={handleExport}
          hideToc
          compact={!isMaximized}
        />
      </div>
    </div>
  );
};
